import type {
  BreakdownField,
  BreakdownLine,
  InsufficientFundsBreakdown,
} from './backend-error.model';

/**
 * Desglose de `INSUFFICIENT_FUNDS` (SPEI). TypeScript puro: CERO Angular.
 * `Intl.NumberFormat` es API de ECMAScript, no de Angular, así que el formato
 * por locale vive aquí; el locale lo pasa quien llama.
 */

/**
 * Orden fijo en que se presentan las líneas al usuario: de dónde sale el dinero,
 * cuánto se va, cuánto se necesita y cuánto falta.
 */
export const BREAKDOWN_FIELD_ORDER: readonly BreakdownField[] = [
  'saldo',
  'en_transito',
  'disponible',
  'monto_envio',
  'comision_con_iva',
  'requerido',
  'faltante',
];

/**
 * Normaliza el `desglose` crudo a números.
 *
 * El backend OMITE los campos que no aplican; NO los manda en cero. Por eso se
 * usa presencia + `Number.isFinite`, y NUNCA `|| 0`: un cero inventado le
 * mentiría al usuario sobre su propio saldo (R31). Un valor no parseable se
 * descarta igual que uno ausente (R32).
 */
export function parseBreakdown(raw: unknown): InsufficientFundsBreakdown {
  const result: { -readonly [K in BreakdownField]?: number } = {};
  if (!isRecord(raw)) return result;

  for (const field of BREAKDOWN_FIELD_ORDER) {
    const value = raw[field];
    if (typeof value === 'number') {
      if (Number.isFinite(value)) result[field] = value;
      continue;
    }
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    // `Number('')` es 0: una cadena vacía es ausencia, no un saldo en cero.
    if (trimmed === '') continue;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) continue;
    result[field] = parsed;
  }
  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Locale de formato por idioma de la UI. El dinero es MXN pase lo que pase. */
export function localeFor(lang: string): string {
  switch (lang) {
    case 'en':
      return 'en-US';
    case 'pt':
      return 'pt-BR';
    case 'es':
      return 'es-MX';
    default:
      return 'es-MX';
  }
}

/**
 * Formatea una cifra como moneda para el locale dado. La divisa se queda en MXN
 * porque el dinero es MXN sin importar el idioma de la interfaz; lo que varía es
 * la presentación (R30).
 */
export function formatAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Convierte el desglose en líneas ordenadas y listas para traducir. Un campo
 * ausente NO produce línea: se omite entero, no se pinta como `0`, `$0.00`,
 * `-`, `undefined` ni `null`.
 */
export function breakdownLines(
  breakdown: InsufficientFundsBreakdown,
  locale: string,
): readonly BreakdownLine[] {
  const lines: BreakdownLine[] = [];
  for (const field of BREAKDOWN_FIELD_ORDER) {
    const value = breakdown[field];
    if (value === undefined) continue;
    lines.push({ field, amount: formatAmount(value, locale) });
  }
  return lines;
}
